// NOTE: Mirrors the Agent Skills limits enforced by the server. The
// authoritative copies are apps/nsai/server/src/skills/skills.constants.ts,
// .../bundle/bundle.constants.ts, .../source/github/github-source.constants.ts
// and .../runtime/skill-runtime.constants.ts. Keep this file in lockstep: a
// client-side bound that is looser than the server's turns a helpful inline
// hint into a surprise 400, and one that is stricter hides valid input.

// NOTE: Manifest and content limits (NSAI-3299 / NSAI-3300 / NSAI-3301).
export const MAX_SKILL_NAME_LENGTH = 64;
export const MAX_SKILL_DESCRIPTION_LENGTH = 1024;
export const MAX_SKILL_BODY_LENGTH = 262_144;
export const MAX_SKILL_RAW_FRONTMATTER_LENGTH = 16_384;
export const MAX_SKILL_LICENSE_LENGTH = 128;
export const MAX_SKILL_COMPATIBILITY_LENGTH = 500;
export const MAX_SKILL_ALLOWED_TOOLS_LENGTH = 4096;

// NOTE: `argument-hint` is one line of help text shown next to a skill in the
// picker, never interpreted, so it is bounded like `license` — the other short
// single-line frontmatter field — rather than like a description.
export const MAX_SKILL_ARGUMENT_HINT_LENGTH = 128;
export const MAX_SKILL_METADATA_ENTRIES = 32;
export const MAX_SKILL_METADATA_KEY_LENGTH = 64;
export const MAX_SKILL_METADATA_VALUE_LENGTH = 512;

// NOTE: Canonical skill names are lowercase kebab-case and are never repaired
// by the server — an invalid name is rejected, not lowercased.
export const SKILL_NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// NOTE: NSAI-6082 — mirrors the server's SKILL_RESERVED_BODY_TAG_PATTERN. The
// runtime frames a loaded skill's body with these two tags, so the server
// refuses a body that spells either one; checking it in the form too means the
// person reads why, instead of a generic invalid-request error from the 400.
export const SKILL_RESERVED_BODY_TAG_PATTERN =
  /<[\s/]*(?:skill_instructions|instructions)(?=[\s/>]|$)/i;

// NOTE: Whole-paste ceiling, measured in UTF-8 bytes rather than characters.
export const MAX_SKILL_PASTE_INPUT_BYTES = 262_144;
export const MAX_SKILL_BODY_TOKENS = 5_000;
export const MAX_SKILL_MANIFEST_ERRORS = 10;

// NOTE: Pagination bounds for GET /skills.
export const MIN_SKILLS_PAGE = 1;
export const DEFAULT_SKILLS_PAGE = 1;
export const DEFAULT_SKILLS_PAGE_SIZE = 20;
export const MAX_SKILLS_PAGE_SIZE = 100;

// NOTE: NSAI-3306 — bound for the `search` query. Equal to the name limit
// because `name` is the ONLY column the server searches: description and body
// are encrypted at rest, so a needle longer than a name can never match.
export const MAX_SKILLS_SEARCH_LENGTH = MAX_SKILL_NAME_LENGTH;

// NOTE: How long a client waits after the last keystroke before asking the
// server again. Search is a server round trip over a paginated list, so a
// per-keystroke request would both hammer the endpoint and make the results
// flicker between pages.
export const SKILLS_SEARCH_DEBOUNCE_MS = 300;

// NOTE: Resource upload limits (NSAI-3303).
export const MAX_SKILL_RESOURCE_BYTES = 26_214_400;
export const MIN_SKILL_RESOURCE_BYTES = 1;
export const MAX_SKILL_VERSION_RESOURCE_TOTAL_BYTES = 104_857_600;
export const MAX_SKILL_VERSION_RESOURCE_COUNT = 100;
export const MAX_SKILL_RESOURCE_PATH_BYTES = 1_024;
export const MAX_SKILL_RESOURCE_PATH_SEGMENTS = 20;
export const MAX_SKILL_RESOURCE_FILE_NAME_LENGTH = 255;
export const MAX_RESOURCE_S3_KEY_LENGTH = 1_024;
export const MAX_RELEASE_SKILL_S3_KEYS_PER_REQUEST = 50;

// NOTE: The two checksum/digest shapes the import DTOs accept. A digest is
// lowercase hex sha256; an upload checksum is the base64 form S3 signs.
export const SKILL_SHA256_HEX_PATTERN = /^[\da-f]{64}$/;
export const SKILL_COMMIT_SHA_HEX_PATTERN = /^[\da-f]{40}$/;
export const SKILL_BASE64_SHA256_PATTERN = /^[A-Za-z0-9+/]{43}=$/;

// NOTE: Bundle limits (NSAI-3310 / NSAI-4961). They count actual streamed
// bytes on the server — the archive's own metadata is never trusted.
export const MAX_SKILL_BUNDLE_ARCHIVE_BYTES = 26_214_400;
export const MAX_SKILL_BUNDLE_EXPANDED_BYTES = 104_857_600;
export const MAX_SKILL_BUNDLE_ENTRY_BYTES = 26_214_400;
export const MAX_SKILL_BUNDLE_ENTRIES = 200;
export const MAX_SKILL_BUNDLE_COMPRESSION_RATIO = 20;
export const MAX_SKILL_BUNDLE_PATH_DEPTH = 10;

// NOTE: GitHub source limits. The repository URL bound is derived the same way
// the server derives it, from the two GitHub name bounds.
export const MAX_GITHUB_OWNER_LENGTH = 39;
export const MAX_GITHUB_REPOSITORY_LENGTH = 100;
export const MAX_GITHUB_REF_LENGTH = 255;
export const MAX_GITHUB_PATH_LENGTH = 512;
export const MAX_GITHUB_REPOSITORY_URL_LENGTH =
  "https://github.com/".length +
  MAX_GITHUB_OWNER_LENGTH +
  "/".length +
  MAX_GITHUB_REPOSITORY_LENGTH;

// NOTE: Runtime limits (NSAI-3304) — what may reach a model prompt. Mirrors the
// server's skill-runtime.constants.ts.
//
// NOTE: NSAI-6772 — the two stopped being one number. Pins (chat picker, agent,
// webhook, hook and workflow forms) go up to 10; a message or run may load up to
// 20 in all, so a full pin set still leaves room for automatic discovery.
export const MAX_LOADED_SKILLS_PER_MESSAGE = 20;
export const MAX_EXPLICIT_SKILL_IDS = 10;

// NOTE: NSAI-5925 — how many refused mentions one sent message records. A draft
// can name any number of skills past the cap, and the record exists so a reader
// can see WHICH ones were asked for and not applied; past a handful the list
// stops informing and starts being a payload. The refused set is the tail BEYOND
// the pinned ones, and the composer resolves at most MAX_SKILL_PICKER_NAMES
// mentions, so at most MAX_SKILL_PICKER_NAMES - MAX_EXPLICIT_SKILL_IDS (10)
// known names can ever be refused — this records every one of them.
export const MAX_REFUSED_SKILL_NAMES = 10;

// NOTE: NSAI-5840 — how many `/name` tokens the composer resolves in one request.
// Mirrors MAX_SKILL_PICKER_NAMES on the server. Larger than the per-message cap on
// purpose: the composer has to RECOGNISE the over-limit tokens too, otherwise it
// cannot tell the user which of them will be ignored.
export const MAX_SKILL_PICKER_NAMES = 20;
export const MAX_CATALOG_DESCRIPTION_CHARS = 320;
export const MAX_SKILL_RESOURCE_READS_PER_MESSAGE = 10;
export const MAX_MODEL_READABLE_RESOURCE_BYTES = 262_144;
export const MAX_SKILL_RESOURCE_TOKENS_PER_READ = 8_000;
export const MAX_SKILL_RESOURCE_TOKENS_PER_MESSAGE = 24_000;
export const SKILL_RESOURCE_DOWNLOAD_URL_EXPIRY_SEC = 300;

// NOTE: Bundle convention from the open specification — everything under
// `scripts/` is executable content, matched on the stored relative path.
export const SKILL_SCRIPT_PATH_PREFIX = "scripts/";

// NOTE: Stored script MIME types, so a script renamed into `references/` is
// still recognized as executable content.
//
// This list mirrors the server's SKILL_SCRIPT_MIME_TYPES
// (apps/nsai/server/src/skills/runtime/skill-runtime.constants.ts) VALUE FOR
// VALUE, and must keep doing so. The server decides `hasScripts` on a list row
// while a client decides it again from a resource list on a detail page; if the
// two lists differ, the same skill reports scripts in one place and not the
// other. Broadening the rule is therefore a SERVER change first — it also
// governs which resources the runtime refuses to read into a prompt.
export const SKILL_SCRIPT_MIME_TYPES: readonly string[] = [
  "text/x-python",
  "text/x-shellscript",
  "application/x-sh",
  "text/javascript",
] as const;

// NOTE: MIME types a person can read as text in the UI. Wider than the
// runtime's model-readable list on purpose: this one only decides which heading
// a file is filed under on the detail page, never what may reach a prompt.
export const READABLE_SKILL_RESOURCE_MIME_TYPES: readonly string[] = [
  "text/markdown",
  "text/plain",
  "text/csv",
  "text/yaml",
  "application/x-yaml",
  "application/json",
  "text/html",
  "application/xml",
  "text/xml",
] as const;

// NOTE: A commit SHA is 40 hex characters and unreadable at full length in a
// list row.
export const SHORT_COMMIT_SHA_LENGTH = 7;

// NOTE: Bytes per mebibyte, for turning a byte limit into the megabyte figure a
// size message interpolates.
export const BYTES_PER_MEBIBYTE = 1_048_576;

// NOTE: The statuses a Skills client has to tell apart, beyond the three
// already in constants/app.ts (403/409/429). The server-error floor used to sit
// here too; NSAI-4624 moved it to constants/app.ts because the retry rule it
// expresses is not Skills-specific.
export const HTTP_STATUS_BAD_REQUEST = 400;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_PAYLOAD_TOO_LARGE = 413;

// NOTE: Keys in the `skills` i18n namespace, named here so a typo is a build
// error at the call site rather than a missing translation at runtime.
export const SKILL_ERROR_MESSAGE_KEYS = {
  forbidden: "errors.notAllowedToWrite",
  forbiddenDelete: "errors.notAllowedToDelete",
  forbiddenRead: "errors.notAllowedToRead",
  notFound: "errors.notFound",
  duplicateName: "import.nameTaken",
  versionConflict: "import.versionConflict",
  tooLarge: "validation.archiveTooLarge",
  rateLimited: "errors.rateLimited",
  invalid: "errors.unexpected",
  saveFailed: "errors.saveFailed",
  cancelled: "importDialog.uploadCancelled",
  unexpected: "errors.unexpected",
  // NOTE: NSAI-3309/NSAI-3311 — a 403 on a sharing or curation route. Separate
  // from `forbidden` because the actionable advice differs: only a skill's owner
  // can manage its shares, and only a curator can publish to the catalog.
  forbiddenShare: "share.errors.share_not_allowed",
  forbiddenCurate: "catalog.errors.catalog_not_allowed_to_curate",
  forbiddenInstall: "catalog.errors.catalog_not_allowed_to_install",
} as const;

// NOTE: Model-visible tool names. Exact strings shared with the server's tool
// registry; a client renders them when a message reports skill tool activity.
export const LOAD_SKILL_TOOL_NAME = "load_skill";
export const READ_SKILL_RESOURCE_TOOL_NAME = "read_skill_resource";
// NOTE: NSAI-3309 — mirrors `RUN_SKILL_SCRIPT_LABEL` in the server's
// `skill-runtime.constants.ts`. It was missing here, so neither client could map it
// to a sentence and the transcript printed the raw handler name — "Using tool:
// run_skill_script..." — to the user mid-run.
export const RUN_SKILL_SCRIPT_TOOL_NAME = "run_skill_script";
// NOTE: NSAI-6772 — mirrors SEARCH_SKILLS_TOOL_NAME on the server. The model
// calls it when a large skill library was listed only in part.
export const SEARCH_SKILLS_TOOL_NAME = "search_skills";
export const SKILL_TOOL_LABELS: readonly string[] = [
  LOAD_SKILL_TOOL_NAME,
  READ_SKILL_RESOURCE_TOOL_NAME,
  RUN_SKILL_SCRIPT_TOOL_NAME,
  SEARCH_SKILLS_TOOL_NAME,
];

// NOTE: The manifest reader stores the instructions under this name; a bundle
// may not carry it as a resource.
export const SKILL_MANIFEST_FILE_NAME = "SKILL.md";

// NOTE: i18n key prefixes the server pairs with each issue code. An issue's
// `messageKey` already arrives fully qualified; these exist so a client can
// recognize which family a key belongs to without parsing the code.
// NOTE: NSAI-3307 — the namespace segment a server-sent issue key carries. A
// client's own bundle IS the `skills` namespace, so this comes off before the
// key reaches t(); the server's whole message catalogue for the three issue
// families is mirrored into this package's skills.json.
export const SKILL_ISSUE_MESSAGE_NAMESPACE_PREFIX = "skills.";

export const SKILL_MANIFEST_MESSAGE_KEY_PREFIX = "skills.manifest.";
export const SKILL_BUNDLE_MESSAGE_KEY_PREFIX = "skills.bundle.";
export const SKILL_SOURCE_MESSAGE_KEY_PREFIX = "skills.source.";

// NOTE: The same family WITHOUT the namespace segment, for a client that is
// already inside the `skills` namespace and is building the key from an
// `errorCode` rather than receiving a fully-qualified `messageKey`.
export const SKILL_SOURCE_MESSAGE_KEY_SUFFIX_PREFIX = "source.";

// NOTE: The Skills REST root. Kept here rather than in the API client so the
// path is stated once for every consumer that has to build a URL.
export const SKILLS_BASE_PATH = "/skills";

// NOTE: NSAI-3306/NSAI-3307 — React Query cache keys, shared so web and mobile
// invalidate the same entries after the same mutation. Every key is scoped by
// the current project uuid at the call site: skills are project-owned, and a
// project switch must not serve the previous project's list from cache.
export const SKILLS_QUERY_KEY = "skills";
export const SKILL_QUERY_KEY = "skill";
export const SKILL_RESOURCES_QUERY_KEY = "skill-resources";

// NOTE: NSAI-4961 — the GitHub update check. Two POSTs under the skill's own
// path: the first reports whether the repository moved and writes nothing, the
// second is the explicit confirmation that appends a new immutable version. There
// is no route that updates without the confirmation.
export const SKILL_SOURCE_GITHUB_PATH_SEGMENT = "source/github";
export const SKILL_SOURCE_GITHUB_CHECK_UPDATE_PATH_SEGMENT = "check-update";
export const SKILL_SOURCE_GITHUB_UPDATE_PATH_SEGMENT = "update";

// NOTE: The check is a POST that costs a GitHub round trip, so its result is
// cached under its own key rather than re-fetched with every render.
export const SKILL_SOURCE_UPDATE_QUERY_KEY = "skill-source-update";

// NOTE: NSAI-3308 — the chat picker reads its own route, not the management
// list: `GET /skills` searches names only and can answer with disabled or
// archived skills, neither of which the runtime would load.
export const SKILLS_CHAT_PICKER_PATH = `${SKILLS_BASE_PATH}/chat-picker`;
export const SKILL_CHAT_PICKER_QUERY_KEY = "skill-chat-picker";

// NOTE: How many picker rows one page carries. Smaller than the management
// page size: the picker is a search box over a bounded list, not a table.
export const SKILL_PICKER_PAGE_SIZE = 20;

// NOTE: The character that opens the skill menu from the composer. A draft that
// starts with it is a menu query rather than message text, and the trigger itself
// is consumed by the composer, so it can never travel with the sent message.
export const SKILL_MENU_TRIGGER = "/";

// NOTE: The socket event carrying live activation records for one assistant
// message. Same name on both clients so a handler cannot drift.
export const SKILL_ACTIVATIONS_SOCKET_EVENT = "skillActivations";

// NOTE: NSAI-3309 — private skill sharing.

// NOTE: The Viewer/Editor roles a share can carry. Declared as a constant list
// so a DTO, a picker and a test all enumerate the same two values.
export const SKILL_SHARES_PATH_SEGMENT = "shares";
export const SKILL_SHARE_CANDIDATES_PATH_SEGMENT = "share-candidates";
export const SKILLS_SHARED_WITH_ME_PATH = `${SKILLS_BASE_PATH}/shared-with-me`;
// NOTE: NSAI-6578 — the RECIPIENT's own share, addressed by the skill: a
// recipient's skill row never carries a share uuid. PATCH sets their discovery
// preference, DELETE removes them from the share.
export const SKILL_MY_SHARE_PATH_SEGMENT = "my-share";

// NOTE: One request may add several recipients at once, bounded so a single call
// cannot fan out into an unbounded number of permission lookups.
export const MAX_SKILL_SHARE_RECIPIENTS_PER_REQUEST = 20;

export const SKILL_SHARES_QUERY_KEY = "skill-shares";
export const SKILL_SHARE_CANDIDATES_QUERY_KEY = "skill-share-candidates";
export const SKILLS_SHARED_WITH_ME_QUERY_KEY = "skills-shared-with-me";

// NOTE: NSAI-3311 — the Organization Catalog. Organization-scoped, so it sits on
// its own root rather than under /skills: a catalog entry belongs to the
// organization, not to the project the request happens to carry.
export const ORGANIZATION_SKILLS_BASE_PATH = "/organization-skills";
export const ORGANIZATION_SKILL_PUBLISH_PATH = `${ORGANIZATION_SKILLS_BASE_PATH}/publish`;
export const ORGANIZATION_SKILL_VERSIONS_PATH_SEGMENT = "versions";
export const ORGANIZATION_SKILL_INSTALL_PATH_SEGMENT = "install";
export const ORGANIZATION_SKILL_UPDATE_PATH_SEGMENT = "update";

// NOTE: NSAI-7391 — the skills marketplace list. Project-scoped (the server
// answers for the x-project-uuid project), so both clients drop it on a
// project switch.
export const SKILL_MARKETPLACE_QUERY_KEY = "skill-marketplace";

export const ORGANIZATION_SKILL_CATALOG_QUERY_KEY =
  "organization-skill-catalog";
export const ORGANIZATION_SKILL_CATALOG_ENTRY_QUERY_KEY =
  "organization-skill-catalog-entry";

// NOTE: i18n key families for the two new stable error-code enums. Built the same
// way the server builds them, so a code maps to a key without a lookup table on
// either side. Namespace-less: a client is already inside the `skills` namespace.
export const SKILL_SHARE_MESSAGE_KEY_PREFIX = "share.errors.";
export const ORGANIZATION_SKILL_MESSAGE_KEY_PREFIX = "catalog.errors.";

// NOTE: Mirrors the server's SKILL_UUID_PATTERN value for value. A selection
// entry that does not match is DROPPED, never repaired and never replaced with a
// near match: silently substituting a different skill would run instructions the
// user did not choose.
export const SKILL_SELECTION_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
